import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Calendar, User, Mail, Phone, Ship, Trash2, Edit, X, Save, Loader2, Plus, DollarSign } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { style: 'bg-yellow-100 text-yellow-800 border border-yellow-300', label: 'En attente' },
  confirmed: { style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', label: 'Confirmee' },
  cancelled: { style: 'bg-red-100 text-red-800 border border-red-300', label: 'Annulee' },
};

const paymentConfig = {
  pending: { style: 'bg-yellow-50 text-yellow-700 border border-yellow-200', label: 'En attente' },
  deposit_paid: { style: 'bg-sky-100 text-sky-800 border border-sky-300', label: 'Acompte paye' },
  fully_paid: { style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', label: 'Paye integralement' },
  refunded: { style: 'bg-red-50 text-red-700 border border-red-200', label: 'Rembourse' },
};

// ─── New Booking Form ───
const NewBookingForm = ({ cruises, token, onCreated, onCancel }) => {
  const emptyPassenger = () => ({ name: '', email: '', phone: '', cabin_number: 1 });
  const [form, setForm] = useState({
    cruise_id: '', cruise_slug: '', client_name: '', email: '', phone: '',
    start_date: '', end_date: '', departure_time: '', arrival_time: '',
    price_per_person: 0, privatization_price: 0,
    caisse_de_bord: 240, amount_paid: 0, notes: '',
  });
  const [passengers, setPassengers] = useState([emptyPassenger()]);
  const [saving, setSaving] = useState(false);

  const handleCruiseChange = (cruiseId) => {
    const c = cruises.find(cr => cr.id === cruiseId);
    if (c) setForm(f => ({ ...f, cruise_id: c.id, cruise_slug: c.slug }));
  };

  const addPassenger = () => setPassengers(p => [...p, emptyPassenger()]);
  const removePassenger = (idx) => setPassengers(p => p.filter((_, i) => i !== idx));
  const updatePassenger = (idx, field, value) => {
    setPassengers(p => p.map((pax, i) => i === idx ? { ...pax, [field]: value } : pax));
  };

  const passengerCount = passengers.length;
  const isPrivatisation = form.privatization_price > 0;
  const basePrice = isPrivatisation ? form.privatization_price : form.price_per_person * passengerCount;
  const totalCdb = form.caisse_de_bord * passengerCount;
  const totalAmount = basePrice + totalCdb;
  const remaining = totalAmount - form.amount_paid;

  // Sync first passenger name as client_name
  const clientName = form.client_name || passengers[0]?.name || '';

  const handleSubmit = async () => {
    if (!form.cruise_id || !clientName || !form.start_date || !form.end_date) {
      toast.error('Remplissez les champs obligatoires (destination, client, dates)');
      return;
    }
    if (passengers.some(p => !p.name.trim())) {
      toast.error('Chaque passager doit avoir un nom');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_name: clientName,
        passenger_count: passengerCount,
        passengers: passengers,
      };
      const res = await axios.post(`${API}/bookings/manual`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-2 border-[#D4AF37] shadow-lg" data-testid="new-booking-form">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-[#0A2540] flex items-center gap-2">
            <Plus className="h-5 w-5 text-[#D4AF37]" /> Nouvelle reservation
          </h3>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onCancel}><X className="h-4 w-4" /></Button>
        </div>

        {/* Destination + Client */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-[#0A2540]">Destination *</Label>
            <Select value={form.cruise_id} onValueChange={handleCruiseChange}>
              <SelectTrigger className="h-9" data-testid="booking-cruise-select"><SelectValue placeholder="Choisir une croisiere" /></SelectTrigger>
              <SelectContent>
                {cruises.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#0A2540]">Nom du client / reservation *</Label>
            <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="h-9" placeholder="Famille Dupont" data-testid="booking-client-name" />
          </div>
        </div>

        {/* Dates + Horaires */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs font-semibold text-[#0A2540]">Date embarquement *</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="h-9" data-testid="booking-start-date" /></div>
          <div><Label className="text-xs">Heure embarquement</Label><Input type="time" value={form.departure_time} onChange={e => setForm(f => ({ ...f, departure_time: e.target.value }))} className="h-9" /></div>
          <div><Label className="text-xs font-semibold text-[#0A2540]">Date debarquement *</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="h-9" data-testid="booking-end-date" /></div>
          <div><Label className="text-xs">Heure debarquement</Label><Input type="time" value={form.arrival_time} onChange={e => setForm(f => ({ ...f, arrival_time: e.target.value }))} className="h-9" /></div>
        </div>

        {/* Contact principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-xs">Email contact principal</Label><Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-9" placeholder="email@exemple.com" /></div>
          <div><Label className="text-xs">Telephone contact principal</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-9" placeholder="06 12 34 56 78" /></div>
        </div>

        {/* Passengers list */}
        <div className="bg-[#0A2540]/5 border border-[#0A2540]/10 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-[#0A2540] uppercase tracking-wider flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" /> Passagers ({passengerCount})
            </h4>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addPassenger} data-testid="add-passenger-btn">
              <Plus className="h-3 w-3 mr-1" /> Ajouter un passager
            </Button>
          </div>
          <div className="space-y-2">
            {passengers.map((pax, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-[#E2E8F0]">
                <div className="w-7 h-7 rounded-full bg-[#0A2540] flex items-center justify-center shrink-0">
                  <span className="text-white text-xs font-bold">{idx + 1}</span>
                </div>
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Input value={pax.name} onChange={e => updatePassenger(idx, 'name', e.target.value)} className="h-8 text-sm" placeholder="Nom complet *" data-testid={`pax-name-${idx}`} />
                  <Input value={pax.email} onChange={e => updatePassenger(idx, 'email', e.target.value)} className="h-8 text-sm" placeholder="Email" />
                  <Input value={pax.phone} onChange={e => updatePassenger(idx, 'phone', e.target.value)} className="h-8 text-sm" placeholder="Telephone" />
                  <Input type="number" min="1" max="4" value={pax.cabin_number} onChange={e => updatePassenger(idx, 'cabin_number', parseInt(e.target.value) || 1)} className="h-8 text-sm w-20" placeholder="Cab." />
                </div>
                {passengers.length > 1 && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 shrink-0" onClick={() => removePassenger(idx)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Financial */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-3">
          <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Tarification</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-emerald-700 font-medium">Prix / passager (EUR)</Label>
              <Input type="number" value={form.price_per_person} onChange={e => setForm(f => ({ ...f, price_per_person: parseFloat(e.target.value) || 0 }))} className="h-9 border-emerald-300" data-testid="booking-price-person" />
            </div>
            <div>
              <Label className="text-xs text-emerald-700 font-medium">OU Prix privatisation (EUR)</Label>
              <Input type="number" value={form.privatization_price} onChange={e => setForm(f => ({ ...f, privatization_price: parseFloat(e.target.value) || 0 }))} className="h-9 border-emerald-300" />
            </div>
            <div>
              <Label className="text-xs text-emerald-700 font-medium">Caisse de bord / passager (EUR)</Label>
              <Input type="number" value={form.caisse_de_bord} onChange={e => setForm(f => ({ ...f, caisse_de_bord: parseFloat(e.target.value) || 0 }))} className="h-9 border-emerald-300" />
            </div>
            <div>
              <Label className="text-xs text-blue-700 font-medium">Somme deja payee (EUR)</Label>
              <Input type="number" value={form.amount_paid} onChange={e => setForm(f => ({ ...f, amount_paid: parseFloat(e.target.value) || 0 }))} className="h-9 border-blue-300" data-testid="booking-amount-paid" />
            </div>
          </div>

          {/* Live summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-emerald-200">
            <div className="text-center">
              <div className="text-[10px] text-emerald-600 uppercase">Base croisiere</div>
              <div className="font-bold text-emerald-800">{basePrice.toLocaleString('fr-FR')} EUR</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-emerald-600 uppercase">Caisse de bord ({passengerCount} x {form.caisse_de_bord})</div>
              <div className="font-bold text-emerald-800">{totalCdb.toLocaleString('fr-FR')} EUR</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] text-[#0A2540] uppercase font-semibold">Total</div>
              <div className="font-bold text-xl text-[#0A2540]">{totalAmount.toLocaleString('fr-FR')} EUR</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase font-semibold text-red-600">Reste a payer</div>
              <div className={`font-bold text-xl ${remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {remaining > 0 ? remaining.toLocaleString('fr-FR') + ' EUR' : 'SOLDE'}
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <Label className="text-xs">Notes internes</Label>
          <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="min-h-[50px] text-sm" placeholder="Infos supplementaires sur le client, demandes speciales..." />
        </div>

        <div className="flex gap-2 pt-2">
          <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" onClick={handleSubmit} disabled={saving} data-testid="submit-booking">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            {saving ? 'Creation en cours...' : `Creer la reservation (${passengerCount} passager${passengerCount > 1 ? 's' : ''})`}
          </Button>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Edit Booking Form ───
const EditBookingForm = ({ booking, token, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    customer_name: booking.customer_name || '',
    customer_email: booking.customer_email || '',
    customer_phone: booking.customer_phone || '',
    booking_status: booking.booking_status || 'pending',
    payment_status: booking.payment_status || 'pending',
    cabin_count: booking.cabin_count || 1,
    total_amount: booking.total_amount || 0,
    deposit_amount: booking.deposit_amount || 0,
    special_requests: booking.special_requests || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bookings/${booking.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Reservation mise a jour');
      onSaved();
    } catch {
      toast.error('Erreur lors de la mise a jour');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 space-y-3 mt-3" data-testid="edit-booking-form">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-[#0A2540] flex items-center gap-2"><Edit className="h-4 w-4" /> Modifier la reservation</h4>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onCancel}><X className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <div><Label className="text-xs font-medium">Nom</Label><Input value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-xs font-medium">Email</Label><Input value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} className="h-8 text-sm" /></div>
        <div><Label className="text-xs font-medium">Telephone</Label><Input value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} className="h-8 text-sm" /></div>
        <div>
          <Label className="text-xs font-medium">Statut reservation</Label>
          <Select value={form.booking_status} onValueChange={v => setForm(f => ({ ...f, booking_status: v }))}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium">Statut paiement</Label>
          <Select value={form.payment_status} onValueChange={v => setForm(f => ({ ...f, payment_status: v }))}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(paymentConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs font-medium">Montant total (EUR)</Label><Input type="number" value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
        <div><Label className="text-xs font-medium">Acompte (EUR)</Label><Input type="number" value={form.deposit_amount} onChange={e => setForm(f => ({ ...f, deposit_amount: parseFloat(e.target.value) || 0 }))} className="h-8 text-sm" /></div>
      </div>
      <div><Label className="text-xs font-medium">Demandes speciales</Label><Textarea value={form.special_requests} onChange={e => setForm(f => ({ ...f, special_requests: e.target.value }))} className="text-sm min-h-[50px]" /></div>
      <div className="flex gap-2">
        <Button size="sm" className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white h-8 text-xs" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onCancel}>Annuler</Button>
      </div>
    </div>
  );
};

// ─── Main Component ───
const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const { token } = useAuth();

  useEffect(() => { fetchAll(); }, [token]);

  const fetchAll = async () => {
    try {
      const [bRes, cRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cruises`),
      ]);
      setBookings(bRes.data);
      setCruises(cRes.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reservation supprimee');
      setConfirmDelete(null);
      fetchAll();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const filteredBookings = bookings.filter(b => filter === 'all' || b.booking_status === filter);

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.booking_status === 'confirmed').length,
    pending: bookings.filter(b => b.booking_status === 'pending').length,
    cancelled: bookings.filter(b => b.booking_status === 'cancelled').length,
    revenue: bookings.filter(b => b.booking_status === 'confirmed').reduce((s, b) => s + (b.total_amount || 0), 0),
  };

  return (
    <AdminLayout title="Reservations">
      <div className="space-y-6" data-testid="admin-bookings">
        {/* Stats */}
        <div className="flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">Total: {stats.total}</div>
          <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">Confirmees: {stats.confirmed}</div>
          <div className="px-3 py-1.5 rounded-full bg-yellow-100 text-sm font-medium text-yellow-700">En attente: {stats.pending}</div>
          <div className="px-3 py-1.5 rounded-full bg-red-100 text-sm font-medium text-red-700">Annulees: {stats.cancelled}</div>
          <div className="px-3 py-1.5 rounded-full bg-[#D4AF37]/20 text-sm font-bold text-[#D4AF37]">CA: {stats.revenue.toLocaleString('fr-FR')} EUR</div>
        </div>

        {/* Actions bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" onClick={() => setShowNew(!showNew)} data-testid="new-booking-btn">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle reservation
          </Button>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="booking-filter-select"><SelectValue placeholder="Filtrer par statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="confirmed">Confirmees</SelectItem>
              <SelectItem value="cancelled">Annulees</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* New booking form */}
        {showNew && (
          <NewBookingForm
            cruises={cruises}
            token={token}
            onCreated={() => { setShowNew(false); fetchAll(); }}
            onCancel={() => setShowNew(false)}
          />
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}</div>
        ) : filteredBookings.length > 0 ? (
          <div className="space-y-4">
            {filteredBookings.map(booking => {
              const bStatus = statusConfig[booking.booking_status] || { style: 'bg-gray-100 text-gray-700', label: booking.booking_status };
              const pStatus = paymentConfig[booking.payment_status] || { style: 'bg-gray-100 text-gray-700', label: booking.payment_status };
              const remaining = (booking.total_amount || 0) - (booking.deposit_amount || 0);
              return (
                <Card key={booking.id} className={`transition-all ${booking.booking_status === 'cancelled' ? 'opacity-60' : ''}`} data-testid={`booking-card-${booking.id}`}>
                  <CardContent className="p-3 sm:p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                      <div className="space-y-2 flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${booking.booking_status === 'confirmed' ? 'bg-emerald-600' : booking.booking_status === 'cancelled' ? 'bg-red-400' : 'bg-yellow-500'}`}>
                            <Ship className="h-5 w-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-[#0A2540] truncate">{booking.cruise_name}</h3>
                            <p className="text-xs text-[#4A5568]">
                              {booking.booking_type === 'cabin' ? 'Cabine' : 'Privatisation'}
                              {booking.passenger_count > 0 && ` | ${booking.passenger_count} passager(s)`}
                              {booking.source === 'manual_admin' && <span className="ml-1 text-[#D4AF37]">(manuelle)</span>}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-[#4A5568]"><User className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{booking.customer_name}</span></div>
                          <div className="flex items-center gap-2 text-[#4A5568]"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{booking.customer_email || '-'}</span></div>
                          <div className="flex items-center gap-2 text-[#4A5568]"><Phone className="h-3.5 w-3.5 shrink-0" />{booking.customer_phone || '-'}</div>
                          <div className="flex items-center gap-2 text-[#4A5568]"><Calendar className="h-3.5 w-3.5 shrink-0" />{booking.start_date}</div>
                        </div>
                        {booking.special_requests && <p className="text-xs text-[#94A3B8] italic bg-[#F8F9FA] p-2 rounded">"{booking.special_requests}"</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="flex gap-2">
                          <Badge className={bStatus.style}>{bStatus.label}</Badge>
                          <Badge className={pStatus.style}>{pStatus.label}</Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#0A2540]">{(booking.total_amount || 0).toLocaleString('fr-FR')} EUR</p>
                          <div className="flex gap-3 text-xs">
                            <span className="text-emerald-600">Paye: {(booking.deposit_amount || 0).toLocaleString('fr-FR')}</span>
                            {remaining > 0 && <span className="text-red-600 font-bold">Reste: {remaining.toLocaleString('fr-FR')}</span>}
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setEditingId(editingId === booking.id ? null : booking.id)} data-testid={`edit-booking-${booking.id}`}>
                            <Edit className="h-3.5 w-3.5 mr-1" />Modifier
                          </Button>
                          {confirmDelete === booking.id ? (
                            <div className="flex items-center gap-1">
                              <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(booking.id)} data-testid={`confirm-delete-booking-${booking.id}`}>Confirmer</Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => setConfirmDelete(booking.id)} data-testid={`delete-booking-${booking.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    {editingId === booking.id && (
                      <EditBookingForm booking={booking} token={token} onSaved={() => { setEditingId(null); fetchAll(); }} onCancel={() => setEditingId(null)} />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
              <p className="text-[#4A5568]">Aucune reservation pour le moment</p>
              <p className="text-xs text-[#94A3B8] mt-1">Cliquez sur "Nouvelle reservation" pour en creer une</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBookings;
